import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import detect from 'detect.js'

import { currentUserSelector } from 'selectors/user'
import { isEmbeddedSelector } from 'selectors/embed'
import { useGetEventTypeQuery } from 'root-api'

import { useTrackCommerce } from './useTrackCommerce'

/**
 * Analytics configuration for package page tracking
 * Maps package types and events to their tracking configurations
 *
 * Structure:
 *   PACKAGE_TYPE: {
 *     event: {
 *       eventName: string,              // Event name (e.g., 'Booking Page Viewed', 'CTA Shown')
 *       bookingPageType: string,         // Transformed booking_page_type property
 *       additionalProperties?: object    // Optional additional properties to merge
 *     }
 *   }
 *
 * To add new events or package types, extend this configuration object.
 */
const ANALYTICS_CONFIG = {
  PAYMENT_LINK: {
    package_checkout: {
      eventName: 'CTA Shown',
      properties: {
        cta_key: 'payment_link_checkout_primary_page',
        cta_text: 'checkout_page',
      },
    },
    package_purchase_complete: null,
  },
  MEETING_PACKAGE: {
    package_checkout: {
      eventName: 'Booking Page Viewed',
      properties: {
        booking_page_type: 'meeting_package_checkout',
      }
    },
    package_purchase_complete: {
      eventName: 'Booking Page Viewed',
      properties: {
        booking_page_type: 'meeting_package_purchase_complete',
      }
    },
  },
}

/**
 * Hook to track package page view events
 *
 * @param {string} event - The event being tracked (e.g., 'package_checkout', 'meeting_package_purchase_complete')
 * @param {Object|null} packageData - Package data object with uuid, package_type, owner_uuid, and owner_type properties
 *
 * @example
 * // For checkout page
 * useTrackPackagePageView('package_checkout', packageData)
 *
 * @example
 * // For success page
 * useTrackPackagePageView('meeting_package_purchase_complete', purchasedPackage)
 */
export const useTrackPackagePageView = (event, packageData) => {
  const track = useTrackCommerce()
  const currentUser = useSelector(currentUserSelector)
  const isEmbedded = useSelector(isEmbeddedSelector)
  const hasTrackedRef = useRef(false)

  const eventTypeUuid = packageData?.variants?.[0]?.items?.[0]?.item_uuid
    || packageData?.purchased_package_items?.[0]?.item_uuid

  const { data: eventType } = useGetEventTypeQuery(
    { eventTypeUuid },
    {
      skip: !eventTypeUuid
    }
  )

  useEffect(() => {
    if (!packageData?.uuid || hasTrackedRef.current) {
      return
    }

    const packageType = packageData?.package_type || 'MEETING_PACKAGE'
    const config = ANALYTICS_CONFIG[packageType][event]

    if (!config) return

    if (packageType === 'MEETING_PACKAGE' && !eventType) {
      return
    }

    const ua = detect.parse(navigator.userAgent)

    const meetingPackageProperties = eventType ? {
      invitee_device: `${ua.device.type} ${ua.os.name}`,
      is_calendly_user: !!(currentUser?.uuid),
      is_embedded: isEmbedded,
      is_owner_session: packageData?.owner_uuid === currentUser?.uuid,
      owner_id: eventType.id,
      owner_type: 'event_type',
    } : {}

    const eventProperties = {
      ...(packageType === 'MEETING_PACKAGE' ? meetingPackageProperties : {}),
      ...config?.properties,
    }

    const dependencies = { user_uuid: packageData?.owner_uuid }

    const eventName = config.eventName

    track(eventName, eventProperties, dependencies)

    hasTrackedRef.current = true
  }, [event, packageData, track, currentUser?.uuid, isEmbedded, eventType])
}
